import { ANIMATION_DURATION, getDefaultMobileImageHeight, IMAGE_HEIGHT_OFFSET } from 'app/components/content-block/constants';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseMobileScrollProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

interface UseMobileScrollResponse {
  imageRef: React.RefObject<HTMLImageElement>;
  initialImageHeight: number;
}

export const useMobileScroll = ({ containerRef }: UseMobileScrollProps): UseMobileScrollResponse => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [initialImageHeight, setInitialImageHeight] = useState(getDefaultMobileImageHeight() - IMAGE_HEIGHT_OFFSET);
  // True while we're driving the image height ourselves (touchmove + animate
  // back). ResizeObserver must skip during this window or the image's own
  // grow-step feeds back into initialImageHeight and the next frame grows
  // from the new baseline — compounding the size on every touchmove.
  const isInteractingRef = useRef(false);

  useEffect(() => {
    const imageElement = imageRef.current;
    if (!imageElement) return undefined;

    const observer = new ResizeObserver(() => {
      if (isInteractingRef.current) return;
      if (imageElement.clientHeight > 0) {
        setInitialImageHeight(imageElement.clientHeight - IMAGE_HEIGHT_OFFSET);
      }
    });

    observer.observe(imageElement);
    return () => observer.disconnect();
  }, []);

  const touchState = useRef({
    startY: 0,
    currentY: 0,
    isDragging: false,
  });

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const scrollElement = containerRef.current;
    if (!scrollElement) return;

    touchState.current.startY = e.touches[0].clientY;
    touchState.current.currentY = touchState.current.startY;
    touchState.current.isDragging = true;
    isInteractingRef.current = true;
    scrollElement.style.transition = 'none';
  }, [containerRef]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const scrollElement = containerRef.current;
    if (!scrollElement || !touchState.current.isDragging) return;

    touchState.current.currentY = e.touches[0].clientY;
    const deltaY = touchState.current.currentY - touchState.current.startY;
    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    const maxScroll = scrollHeight - clientHeight;

    const isHittingTopBoundary = (scrollTop >= maxScroll && deltaY < 0);
    const isHittingBottomBoundary = (scrollTop <= 0 && deltaY > 0);

    if (!(isHittingTopBoundary || isHittingBottomBoundary)) {
      touchState.current.startY = e.touches[0].clientY;
      return;
    }

    if (isHittingTopBoundary || isHittingBottomBoundary) {
      e.preventDefault();
      touchState.current.currentY = e.touches[0].clientY;
      const resistance = (Math.abs(deltaY) / ANIMATION_DURATION) ** 0.1;
      const bounceDistance = deltaY * resistance * 0.3;
      scrollElement.style.transform = `translateY(${bounceDistance}px)`;

      const imageElement = imageRef.current;
      if (imageElement && isHittingBottomBoundary) {
        imageElement.style.height = `${initialImageHeight + bounceDistance + IMAGE_HEIGHT_OFFSET}px`;
      }
    }
  }, [containerRef, initialImageHeight]);

  const handleTouchEnd = useCallback(() => {
    const scrollElement = containerRef.current;
    const imageElement = imageRef.current;

    if (!scrollElement || !imageElement || !touchState.current.isDragging) return;

    touchState.current.isDragging = false;

    const targetImageHeight = initialImageHeight;

    const startTime = performance.now();

    const easeOutCubic = (t: number) => 1 - (1 - t) ** 0.5;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const scrollProgress = Math.min(elapsed / ANIMATION_DURATION, 1);

      const scrollEasedProgress = easeOutCubic(scrollProgress);

      const currentScrollY = (1 - scrollEasedProgress) * parseFloat(
        scrollElement.style.transform.replace(/[^\d.-]/g, '') || '0',
      );
      scrollElement.style.transform = `translateY(${currentScrollY}px)`;

      const { top: scrollTop } = scrollElement.getBoundingClientRect();

      const targetScale = (scrollTop + initialImageHeight) / initialImageHeight;
      imageElement.style.height = targetScale > 1 ? `${targetScale * initialImageHeight + IMAGE_HEIGHT_OFFSET}px` : `${initialImageHeight + IMAGE_HEIGHT_OFFSET}px`;

      if (scrollProgress < 1) {
        requestAnimationFrame(animate);
      } else {
        scrollElement.style.transition = '';
        scrollElement.style.transform = '';
        imageElement.style.transition = '';
        imageElement.style.height = `${targetImageHeight + IMAGE_HEIGHT_OFFSET}px`;
        isInteractingRef.current = false;
      }
    };

    requestAnimationFrame(animate);
  }, [containerRef, initialImageHeight]);

  useEffect(() => {
    const scrollElement = containerRef.current;
    if (!scrollElement) return undefined;

    scrollElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    scrollElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    scrollElement.addEventListener('touchend', handleTouchEnd);

    return () => {
      scrollElement.removeEventListener('touchstart', handleTouchStart);
      scrollElement.removeEventListener('touchmove', handleTouchMove);
      scrollElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, containerRef]);

  return { imageRef, initialImageHeight };
};
